import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";

const RouteManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [lookupData, setLookupData] = useState({ drivers: [], vehicles: [], pickup_points: [], bus_timings: [], drop_points: [] });
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    bus_timings_fk: "All",
    pickup_location_fk: "All",
    drop_point_fk: [],
    driver_lookup_fk: "All",
    vehicle_lookup_fk: "All"
  });
  const [formData, setFormData] = useState({
    travel_date: "",
    bus_timings_fk: "",
    pickup_location_fk: "",
    drop_point_fk: "",
    driver_lookup_fk: "",
    vehicle_lookup_fk: "",
    comments: ""
  });
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({});

  const fetchLookupData = async () => {
    try {
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/getLookupData`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        setLookupData(result.lookup_data);
      } else {
        toast.error(result.message || "Failed to fetch lookup data");
      }
    } catch (error) {
      console.error("Error fetching lookup data:", error);
      toast.error("Failed to fetch lookup data");
    }
  };

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/getAllRouteDetails`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        setRoutes(result.route_details || []);
      } else {
        toast.error(result.message || "Failed to fetch route details");
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      toast.error("Failed to fetch route details");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutesWithFilters = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      
      // Date fields are required by backend
      data.append('from_date', filters.from_date || "");
      data.append('to_date', filters.to_date || "");
      
      // Other filter fields
      data.append('bus_timings_fk', filters.bus_timings_fk || "All");
      data.append('pickup_location_fk', filters.pickup_location_fk || "All");
      
      // Handle drop_point_fk: can be single value or array
      if (filters.drop_point_fk && filters.drop_point_fk.length > 0) {
        // Send array using array notation for form data
        filters.drop_point_fk.forEach(id => {
          data.append('drop_point_fk[]', id.toString());
        });
      } else {
        // No selection or empty array - send "All"
        data.append('drop_point_fk', "All");
      }
      
      data.append('driver_lookup_fk', filters.driver_lookup_fk || "All");
      data.append('vehicle_lookup_fk', filters.vehicle_lookup_fk || "All");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/getRouteDetailsWithFilters`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        setRoutes(result.route_details || []);
        setStatistics(result.statistics || {});
      } else {
        toast.error(result.message || "Failed to fetch filtered route details");
      }
    } catch (error) {
      console.error("Error fetching filtered routes:", error);
      toast.error("Failed to fetch filtered route details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookupData();
    fetchRoutes();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFilterChange = (e) => {
    const { name } = e.target;
    
    if (name === 'drop_point_fk') {
      // Handle multi-select for drop points
      const selectedOptions = Array.from(e.target.selectedOptions);
      const selectedValues = selectedOptions.map(option => parseInt(option.value));
      setFilters({
        ...filters,
        [name]: selectedValues,
      });
    } else {
      setFilters({
        ...filters,
        [name]: e.target.value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.travel_date || !formData.bus_timings_fk || !formData.pickup_location_fk || 
        !formData.drop_point_fk || !formData.driver_lookup_fk || !formData.vehicle_lookup_fk) {
      toast.error("All fields are required");
      return;
    }

    setLoading(true);
    try {
      const endpoint = editingRoute 
        ? `${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/updateRouteDetail`
        : `${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/createRouteDetail`;

      const data = new URLSearchParams();
      if (editingRoute) {
        data.append('route_id', editingRoute.route_id);
      }
      data.append('travel_date', formData.travel_date);
      data.append('bus_timings_fk', formData.bus_timings_fk);
      data.append('pickup_location_fk', formData.pickup_location_fk);
      data.append('drop_point_fk', formData.drop_point_fk);
      data.append('driver_lookup_fk', formData.driver_lookup_fk);
      data.append('vehicle_lookup_fk', formData.vehicle_lookup_fk);
      data.append('comments', formData.comments);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        setShowModal(false);
        setFormData({
          travel_date: "",
          bus_timings_fk: "",
          pickup_location_fk: "",
          drop_point_fk: "",
          driver_lookup_fk: "",
          vehicle_lookup_fk: "",
          comments: ""
        });
        setEditingRoute(null);
        fetchRoutes();
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error saving route:", error);
      toast.error("Failed to save route");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      travel_date: route.travel_date,
      bus_timings_fk: route.bus_timings_fk,
      pickup_location_fk: route.pickup_location_fk,
      drop_point_fk: route.drop_point_fk,
      driver_lookup_fk: route.driver_lookup_fk,
      vehicle_lookup_fk: route.vehicle_lookup_fk,
      comments: route.comments || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (routeId) => {
    if (window.confirm("Are you sure you want to delete this route detail?") === false) {
      return;
    }

    setLoading(true);
    try {
      const data = new URLSearchParams();
      data.append('route_id', routeId);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/deleteRouteDetail`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        fetchRoutes();
      } else {
        toast.error(result.message || "Failed to delete route detail");
      }
    } catch (error) {
      console.error("Error deleting route:", error);
      toast.error("Failed to delete route detail");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const data = new URLSearchParams();
      
      // Date fields are required by backend
      data.append('from_date', filters.from_date || "");
      data.append('to_date', filters.to_date || "");
      
      // Other filter fields
      data.append('bus_timings_fk', filters.bus_timings_fk || "All");
      data.append('pickup_location_fk', filters.pickup_location_fk || "All");
      
      // Handle drop_point_fk: can be single value or array
      if (filters.drop_point_fk && filters.drop_point_fk.length > 0) {
        // Send array using array notation for form data
        filters.drop_point_fk.forEach(id => {
          data.append('drop_point_fk[]', id.toString());
        });
      } else {
        // No selection or empty array - send "All"
        data.append('drop_point_fk', "All");
      }
      
      data.append('driver_lookup_fk', filters.driver_lookup_fk || "All");
      data.append('vehicle_lookup_fk', filters.vehicle_lookup_fk || "All");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/MonthlyRouteDriverDetails/generateExcelRouteDetails`, {
        method: "POST",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success(result.message);
        // Create download link
        const link = document.createElement('a');
        link.href = result.download_url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error(result.message || "Failed to generate Excel report");
      }
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Failed to generate Excel report");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRoute(null);
    setFormData({
      travel_date: "",
      bus_timings_fk: "",
      pickup_location_fk: "",
      drop_point_fk: "",
      driver_lookup_fk: "",
      vehicle_lookup_fk: "",
      comments: ""
    });
  };

  const clearFilters = () => {
    setFilters({
      from_date: "",
      to_date: "",
      bus_timings_fk: "All",
      pickup_location_fk: "All",
      drop_point_fk: [],
      driver_lookup_fk: "All",
      vehicle_lookup_fk: "All"
    });
    fetchRoutes();
  };

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="mb-0">Route Driver Details Management</h5>
                  <button
                    class="btn d-inline-flex btn-sm btn-primary"
                    onClick={() => setShowModal(true)}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Route Detail</span>
                  </button>
                </div>

                {/* Filters Section */}
                <div class="card-body border-bottom">
                  <div class="row g-3">
                    <div class="col-md-3">
                      <label class="form-label">From Date</label>
                      <input
                        type="date"
                        class="form-control"
                        name="from_date"
                        value={filters.from_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">To Date</label>
                      <input
                        type="date"
                        class="form-control"
                        name="to_date"
                        value={filters.to_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Bus Timing</label>
                      <select
                        class="form-select"
                        name="bus_timings_fk"
                        value={filters.bus_timings_fk}
                        onChange={handleFilterChange}
                      >
                        <option value="All">All Timings</option>
                        {lookupData.bus_timings.map(timing => (
                          <option key={timing.time_id} value={timing.time_id}>
                            {timing.time_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Pickup Point</label>
                      <select
                        class="form-select"
                        name="pickup_location_fk"
                        value={filters.pickup_location_fk}
                        onChange={handleFilterChange}
                      >
                        <option value="All">All Pickup Points</option>
                        {lookupData.pickup_points.map(point => (
                          <option key={point.pickup_point_id} value={point.pickup_point_id}>
                            {point.pickup_point_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div class="row g-3 mt-2">
                    <div class="col-md-3">
                      <label class="form-label">Drop Point</label>
                      <select
                        class="form-select"
                        name="drop_point_fk"
                        multiple
                        size="4"
                        value={filters.drop_point_fk && filters.drop_point_fk.length > 0 ? filters.drop_point_fk.map(id => id.toString()) : []}
                        onChange={handleFilterChange}
                        style={{ minHeight: '100px' }}
                      >
                        {lookupData.drop_points.map(point => (
                          <option key={point.drop_point_id} value={point.drop_point_id}>
                            {point.drop_point_name}
                          </option>
                        ))}
                      </select>
                      <small class="form-text text-muted">
                        Hold Ctrl/Cmd to select multiple
                      </small>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Driver</label>
                      <select
                        class="form-select"
                        name="driver_lookup_fk"
                        value={filters.driver_lookup_fk}
                        onChange={handleFilterChange}
                      >
                        <option value="All">All Drivers</option>
                        {lookupData.drivers.map(driver => (
                          <option key={driver.driver_lookup_id} value={driver.driver_lookup_id}>
                            {driver.driver_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Vehicle</label>
                      <select
                        class="form-select"
                        name="vehicle_lookup_fk"
                        value={filters.vehicle_lookup_fk}
                        onChange={handleFilterChange}
                      >
                        <option value="All">All Vehicles</option>
                        {lookupData.vehicles.map(vehicle => (
                          <option key={vehicle.vehicle_lookup_id} value={vehicle.vehicle_lookup_id}>
                            {vehicle.vehicle_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div class="row g-3 mt-2">
                    <div class="col-md-12">
                      <button
                        class="btn btn-primary me-2"
                        onClick={fetchRoutesWithFilters}
                        disabled={loading}
                      >
                        <i class="bi bi-search me-1"></i>
                        Apply Filters
                      </button>
                      <button
                        class="btn btn-secondary me-2"
                        onClick={clearFilters}
                        disabled={loading}
                      >
                        <i class="bi bi-x-circle me-1"></i>
                        Clear Filters
                      </button>
                      <button
                        class="btn btn-success"
                        onClick={handleExportExcel}
                        disabled={loading}
                      >
                        <i class="bi bi-file-earmark-excel me-1"></i>
                        Export Excel
                      </button>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                {Object.keys(statistics).length > 0 && (
                  <div class="card-body border-bottom bg-light">
                    <div class="row">
                      <div class="col-md-4">
                        <div class="text-center">
                          <h6 class="text-muted">Total Routes</h6>
                          <h4 class="text-primary">{statistics.total_routes}</h4>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="text-center">
                          <h6 class="text-muted">Unique Drivers</h6>
                          <h4 class="text-success">{statistics.unique_drivers}</h4>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="text-center">
                          <h6 class="text-muted">Unique Vehicles</h6>
                          <h4 class="text-info">{statistics.unique_vehicles}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div class="table-responsive">
                  <table class="table table-hover table-nowrap">
                    <thead class="thead-light">
                      <tr>
                        <th scope="col">Route ID</th>
                        <th scope="col">Travel Date</th>
                        <th scope="col">Bus Timing</th>
                        <th scope="col">Pickup Point</th>
                        <th scope="col">Drop Point</th>
                        <th scope="col">Driver</th>
                        <th scope="col">Vehicle</th>
                        <th scope="col">Comments</th>
                        <th scope="col">Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colspan="8" class="text-center">
                            <div class="spinner-border" role="status">
                              <span class="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : routes.length > 0 ? (
                        routes.map((route) => (
                          <tr key={route.route_id}>
                            <td>{route.route_id}</td>
                            <td>{new Date(route.travel_date).toLocaleDateString()}</td>
                            <td>{route.time_name || route.bus_timings_fk}</td>
                            <td>{route.pickup_point_name || route.pickup_location_fk}</td>
                            <td>{route.drop_point_name || route.drop_point_fk}</td>
                            <td>{route.driver_name}</td>
                            <td>{route.vehicle_name}</td>
                            <td>
                              <span title={route.comments || 'No comments'}>
                                {route.comments ? (
                                  route.comments.length > 30 ? 
                                    `${route.comments.substring(0, 30)}...` : 
                                    route.comments
                                ) : (
                                  <span class="text-muted">-</span>
                                )}
                              </span>
                            </td>
                            <td>
                              <span class="badge badge-lg badge-dot">
                                {route.status == 0 ? (
                                  <>
                                    <i style={{ backgroundColor: "gray" }}></i>
                                    Inactive
                                  </>
                                ) : (
                                  <>
                                    <i className="bg-success"></i>
                                    Active
                                  </>
                                )}
                              </span>
                            </td>
                            <td class="text-end">
                              <button
                                onClick={() => handleEdit(route)}
                                class="btn btn-sm btn-neutral"
                                style={{ marginRight: "0.5rem" }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                class="btn btn-sm btn-square btn-neutral text-danger-hover"
                                onClick={() => handleDelete(route.route_id)}
                                title="Delete Route"
                              >
                                <i class="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colspan="8" class="text-center text-muted">No route details found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div class="card-footer border-0 py-5">
                  <button
                    class="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={() => setShowModal(true)}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Route Detail</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Add/Edit Route Modal */}
      {showModal && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  {editingRoute ? 'Edit Route Detail' : 'Add New Route Detail'}
                </h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div class="modal-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label for="travel_date" class="form-label">Travel Date</label>
                      <input
                        type="date"
                        class="form-control"
                        id="travel_date"
                        name="travel_date"
                        value={formData.travel_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div class="col-md-6">
                      <label for="bus_timings_fk" class="form-label">Bus Timing</label>
                      <select
                        class="form-select"
                        id="bus_timings_fk"
                        name="bus_timings_fk"
                        value={formData.bus_timings_fk}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Bus Timing</option>
                        {lookupData.bus_timings.map(timing => (
                          <option key={timing.time_id} value={timing.time_id}>
                            {timing.time_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label for="pickup_location_fk" class="form-label">Pickup Point</label>
                      <select
                        class="form-select"
                        id="pickup_location_fk"
                        name="pickup_location_fk"
                        value={formData.pickup_location_fk}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Pickup Point</option>
                        {lookupData.pickup_points.map(point => (
                          <option key={point.pickup_point_id} value={point.pickup_point_id}>
                            {point.pickup_point_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label for="drop_point_fk" class="form-label">Drop Point</label>
                      <select
                        class="form-select"
                        id="drop_point_fk"
                        name="drop_point_fk"
                        value={formData.drop_point_fk}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Drop Point</option>
                        {lookupData.drop_points.map(point => (
                          <option key={point.drop_point_id} value={point.drop_point_id}>
                            {point.drop_point_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label for="driver_lookup_fk" class="form-label">Driver</label>
                      <select
                        class="form-select"
                        id="driver_lookup_fk"
                        name="driver_lookup_fk"
                        value={formData.driver_lookup_fk}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Driver</option>
                        {lookupData.drivers.map(driver => (
                          <option key={driver.driver_lookup_id} value={driver.driver_lookup_id}>
                            {driver.driver_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label for="vehicle_lookup_fk" class="form-label">Vehicle</label>
                      <select
                        class="form-select"
                        id="vehicle_lookup_fk"
                        name="vehicle_lookup_fk"
                        value={formData.vehicle_lookup_fk}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Vehicle</option>
                        {lookupData.vehicles.map(vehicle => (
                          <option key={vehicle.vehicle_lookup_id} value={vehicle.vehicle_lookup_id}>
                            {vehicle.vehicle_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div class="col-md-12">
                      <label for="comments" class="form-label">Comments</label>
                      <textarea
                        class="form-control"
                        id="comments"
                        name="comments"
                        value={formData.comments}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Enter any special instructions or comments for this route (optional)"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button 
                    type="button" 
                    class="btn btn-secondary" 
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      editingRoute ? 'Update Route' : 'Add Route'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RouteManagement;
